import { useEffect, useState } from "react";
import api, { formatINR, todayISO, API, formatDate } from "../lib/api";
import { Plus, Trash2, X, Upload, Download, Paperclip, Pencil, IndianRupee } from "lucide-react";
import { DriveDocuments } from "../components/DriveDocuments";
import { UpiPayModal } from "../components/UpiPayModal";
import { usePolling } from "../lib/usePolling";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";
import { uid } from "../lib/uid";

const CATEGORIES = ["Fuel", "Toll", "Maintenance", "Salary", "Office", "Misc"];

const blank = {
  expense_date: todayISO(),
  category: "Fuel",
  amount: 0,
  vehicle_number: "",
  reason: "",
  notes: "",
  lr_ids: [],
  allocations: [],
  receipt_image: "",
  bank_account_id: "",
  bank_account_label: "",
  source_txn_id: "",
};

export default function Expenses() {
  const [items, setItems] = useState([]);
  const paged = usePagination(items, 25);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [docsExpense, setDocsExpense] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [lrs, setLrs] = useState([]);

  useEffect(() => { api.get("/lrs").then((r) => setLrs(r.data)).catch(() => {}); }, []);
  const lrMap = Object.fromEntries(lrs.map((l) => [l.id, l]));
  const [bankAccounts, setBankAccounts] = useState([]);
  useEffect(() => { api.get("/bank-accounts").then((r) => setBankAccounts(r.data)).catch(() => {}); }, []);
  const [debits, setDebits] = useState([]);
  useEffect(() => {
    api.get("/bank/transactions").then((r) => {
      const arr = Array.isArray(r.data) ? r.data : (r.data?.transactions || []);
      setDebits(arr.filter((t) => Number(t.debit) > 0 && (t.tally_status || "untallied") !== "tallied"));
    }).catch(() => {});
  }, [showForm]);

  async function load() {
    const params = {};
    if (filterCat) params.category = filterCat;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (searchText) params.q = searchText;
    const { data } = await api.get("/expenses", { params });
    setItems(data);
  }
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [filterCat, startDate, endDate, searchText]); // eslint-disable-line
  usePolling(load, 8000, !showForm);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function onFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      alert("Receipt image must be under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("receipt_image", String(reader.result));
    reader.readAsDataURL(f);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, allocations: (form.allocations || []).map(({ _uid, ...a }) => a) };
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
      } else {
        await api.post("/expenses", payload);
      }
      setShowForm(false);
      setForm(blank);
      setEditingId(null);
      load();
    } finally { setSaving(false); }
  }

  function openEdit(ex) {
    setForm({ ...blank, ...ex });
    setEditingId(ex.id);
    setShowForm(true);
  }

  async function remove(id) {
    if (!confirm("Delete expense?")) return;
    await api.delete(`/expenses/${id}`); load();
  }

  const total = items.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div data-testid="expenses-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Cash Flow</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">Expenses</h1>
          <p className="text-sm text-[#68665E] mt-2">Total filtered: <span className="font-medium text-[#1A1A18]" data-testid="expenses-total">{formatINR(total)}</span></p>
        </div>
        <div className="flex gap-2">
          <a
            href={`${API}/export/expenses.csv${(filterCat || startDate || endDate) ? "?" + new URLSearchParams({
              ...(filterCat && { category: filterCat }),
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            }).toString() : ""}`}
            target="_blank" rel="noreferrer" className="btn-secondary" data-testid="export-expenses-csv"
          ><Download className="w-4 h-4" /> Export CSV</a>
          <a
            href={`${API}/export/expenses.xlsx${(filterCat || startDate || endDate) ? "?" + new URLSearchParams({
              ...(filterCat && { category: filterCat }),
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            }).toString() : ""}`}
            target="_blank" rel="noreferrer" className="btn-secondary" data-testid="export-expenses-xlsx"
          ><Download className="w-4 h-4" /> Export Excel</a>
          <button className="btn-primary" onClick={()=>{setForm(blank); setEditingId(null); setShowForm(true);}} data-testid="create-expense-button"><Plus className="w-4 h-4" /> Add Expense</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-end">
        <div>
          <label className="input-label">Search</label>
          <input className="input-field w-64" placeholder="Reason, vehicle, LR, party..." value={searchText} onChange={(e)=>setSearchText(e.target.value)} data-testid="expense-search" />
        </div>
        <div>
          <label className="input-label">Category</label>
          <select className="input-field w-48" value={filterCat} onChange={(e)=>setFilterCat(e.target.value)} data-testid="expense-category-filter">
            <option value="">All categories</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="input-label">From</label>
          <input type="date" className="input-field w-44" value={startDate} onChange={(e)=>setStartDate(e.target.value)} data-testid="expense-start-date" />
        </div>
        <div>
          <label className="input-label">To</label>
          <input type="date" className="input-field w-44" value={endDate} onChange={(e)=>setEndDate(e.target.value)} data-testid="expense-end-date" />
        </div>
        {(startDate || endDate || filterCat || searchText) && (
          <button className="btn-ghost text-sm" onClick={()=>{setStartDate(""); setEndDate(""); setFilterCat(""); setSearchText("");}} data-testid="expense-clear-filters">Clear</button>
        )}
      </div>

      <Pagination {...paged} testId="expenses-pagination" />
      <div className="table-wrap">
        <table className="w-full">
          <thead>
            <tr>
              <th className="table-th">Date</th>
              <th className="table-th">Category</th>
              <th className="table-th">Vehicle</th>
              <th className="table-th">Reason</th>
              <th className="table-th">Notes</th>
              <th className="table-th">Receipt</th>
              <th className="table-th">Amount</th>
              <th className="table-th text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={8}>No expenses recorded.</td></tr>
            ) : paged.pageItems.map((ex) => (
              <tr key={ex.id} className="table-row" data-testid={`expense-row-${ex.id}`}>
                <td className="table-td">{formatDate(ex.expense_date)}</td>
                <td className="table-td"><span className="badge-neutral">{ex.category}</span></td>
                <td className="table-td font-mono text-xs">{ex.vehicle_number || "—"}</td>
                <td className="table-td text-[#1A1A18] max-w-xs truncate">{ex.reason || "—"}</td>
                <td className="table-td text-[#68665E] max-w-xs truncate">{ex.notes || "—"}</td>
                <td className="table-td">
                  {ex.receipt_image ? (
                    <button className="btn-ghost" onClick={() => setPreview(ex.receipt_image)} data-testid={`expense-view-receipt-${ex.id}`}>
                      <img src={ex.receipt_image} alt="receipt" className="w-10 h-10 object-cover border border-[#E5E3DB]" />
                    </button>
                  ) : <span className="text-[#A09E96]">—</span>}
                </td>
                <td className="table-td font-medium">{formatINR(ex.amount)}</td>
                <td className="table-td text-right">
                  <button className="btn-ghost text-[#C84B31]" onClick={() => setPayModal({ payee_name: ex.paid_to || "", upi_id: "", amount: ex.amount, note: [ex.category, ex.reason].filter(Boolean).join(" — "), category: ex.category || "", ref_type: "expense", ref_id: ex.id, ref_number: `${ex.category || "Expense"} ${formatDate(ex.expense_date)}` })} title="Pay via UPI" data-testid={`expense-pay-upi-${ex.id}`}><IndianRupee className="w-4 h-4" /></button>
                  <button className="btn-ghost" onClick={() => openEdit(ex)} title="Edit expense" data-testid={`expense-edit-${ex.id}`}><Pencil className="w-4 h-4" /></button>
                  <button className="btn-ghost" onClick={() => setDocsExpense(ex)} title="Drive receipts" data-testid={`expense-docs-${ex.id}`}><Paperclip className="w-4 h-4" /></button>
                  <button className="btn-ghost text-[#8A2526]" onClick={() => remove(ex.id)} data-testid={`expense-delete-${ex.id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center overflow-y-auto py-8" data-testid="expense-form-modal">
          <div className="bg-white w-full max-w-2xl mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <h2 className="font-headings text-xl font-bold">{editingId ? "Edit Expense" : "Add Expense"}</h2>
              <button className="btn-ghost" onClick={()=>{setShowForm(false); setEditingId(null);}}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className="input-label">Date</label><input type="date" className="input-field" value={form.expense_date} onChange={(e)=>set("expense_date", e.target.value)} required data-testid="expense-date-input" /></div>
                <div><label className="input-label">Category</label>
                  <select className="input-field" value={form.category} onChange={(e)=>set("category", e.target.value)} data-testid="expense-category-input">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div><label className="input-label">Amount (₹)</label><input type="number" step="0.01" className="input-field" value={form.amount} onChange={(e)=>set("amount", Number(e.target.value))} required data-testid="expense-amount-input" /></div>
                <div><label className="input-label">Vehicle (Optional)</label><input className="input-field font-mono uppercase" value={form.vehicle_number} onChange={(e)=>set("vehicle_number", e.target.value.toUpperCase())} data-testid="expense-vehicle-input" /></div>
              </div>
              <div><label className="input-label">Paid from account</label>
                <select className="input-field" value={form.bank_account_id} onChange={(e)=>{ const acc = bankAccounts.find((b)=>b.id===e.target.value); set("bank_account_id", e.target.value); set("bank_account_label", acc?.label || ""); }} data-testid="expense-bank-select">
                  <option value="">— Not specified —</option>
                  {bankAccounts.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
                </select>
                {bankAccounts.length === 0 && <p className="text-[11px] text-[#A09E96] mt-1">Add bank accounts under Vehicles &amp; Drivers (Masters) to select them here.</p>}
              </div>
              <div><label className="input-label">Reason</label><input className="input-field" value={form.reason} onChange={(e)=>set("reason", e.target.value)} placeholder="Why was this spent?" data-testid="expense-reason-input" /></div>

              <div>
                <label className="input-label">Pull amount from a bank debit (optional)</label>
                <select className="input-field" value={form.source_txn_id} data-testid="expense-source-txn-select"
                  onChange={(e)=>{ const t = debits.find((d)=>d.id===e.target.value); if(!t){ set("source_txn_id",""); return;} setForm((f)=>({ ...f, source_txn_id: t.id, amount: Number(t.debit)||f.amount, bank_account_id: t.bank_account_id||f.bank_account_id, bank_account_label: t.account_label||t.bank_name||f.bank_account_label, reason: f.reason || t.description || "", expense_date: t.date || f.expense_date })); }}>
                  <option value="">— Not from a bank row —</option>
                  {debits.map((d)=>(<option key={d.id} value={d.id}>{formatDate(d.date)} · {formatINR(d.debit)} · {(d.description||"").slice(0,40)}</option>))}
                </select>
                {form.source_txn_id && <p className="text-[11px] text-[#2F6F4E] mt-1">This bank row will be marked tallied when you save. Use “Split Expense” below to divide it across LRs.</p>}
              </div>

              <div>
                <label className="input-label">Link to Lorry Receipts (for route &amp; transit tracking)</label>
                <select className="input-field" value="" onChange={(e)=>{ const id=e.target.value; if(id && !form.lr_ids.includes(id)) set("lr_ids",[...form.lr_ids,id]); }} data-testid="expense-link-lr-select">
                  <option value="">+ Add an LR…</option>
                  {lrs.filter((l)=>!form.lr_ids.includes(l.id)).map((l)=>(
                    <option key={l.id} value={l.id}>{l.lr_number} — {l.from_location}→{l.to_location}</option>
                  ))}
                </select>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.lr_ids.map((id)=>{ const l=lrMap[id]; return (
                    <span key={id} className="badge-neutral inline-flex items-center gap-1" data-testid={`expense-lr-chip-${id}`}>
                      {l ? l.lr_number : id}{l ? ` (${l.from_location}→${l.to_location})` : ""}
                      <button type="button" onClick={()=>set("lr_ids", form.lr_ids.filter((x)=>x!==id))} className="text-[#8A2526]">×</button>
                    </span>
                  ); })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="input-label mb-0">Split Expense (optional) — allocate across LRs / staff / drivers</label>
                  <div className="flex items-center gap-3">
                    <button type="button" className="btn-ghost text-xs" data-testid="expense-even-split"
                      onClick={()=>{const arr=(form.allocations||[]);if(!arr.length)return;const each=Math.round((Number(form.amount||0)/arr.length)*100)/100;set("allocations",arr.map((a,i)=>({...a,amount:i===arr.length-1?Math.round((Number(form.amount||0)-each*(arr.length-1))*100)/100:each})));}}>÷ Equally</button>
                    <button type="button" className="btn-ghost text-xs" onClick={()=>set("allocations",[...(form.allocations||[]),{_uid:uid(),type:"lr",ref_id:"",ref_label:"",amount:0}])} data-testid="expense-add-split">+ Add Split</button>
                  </div>
                </div>
                {(form.allocations||[]).map((a,i)=>{ const upd=(k,v)=>{const arr=form.allocations.slice();arr[i]={...arr[i],[k]:v};set("allocations",arr);};
                  return (
                    <div key={a._uid ?? i} className="grid grid-cols-12 gap-2 mb-2 items-center" data-testid={`expense-split-row-${i}`}>
                      <select className="input-field col-span-3" value={a.type} onChange={(e)=>upd("type",e.target.value)} data-testid={`expense-split-type-${i}`}>
                        <option value="lr">LR</option><option value="staff">Staff</option><option value="driver">Driver</option>
                      </select>
                      {a.type==="lr" ? (
                        <select className="input-field col-span-6" value={a.ref_id} onChange={(e)=>{const l=lrMap[e.target.value];upd("ref_id",e.target.value);const arr=form.allocations.slice();arr[i]={...arr[i],ref_id:e.target.value,ref_label:l?l.lr_number:""};set("allocations",arr);}}>
                          <option value="">Select LR…</option>
                          {lrs.map((l)=><option key={l.id} value={l.id}>{l.lr_number}</option>)}
                        </select>
                      ) : (
                        <input className="input-field col-span-6" placeholder="Name" value={a.ref_label} onChange={(e)=>upd("ref_label",e.target.value)} data-testid={`expense-split-label-${i}`} />
                      )}
                      <input type="number" step="0.01" className="input-field col-span-2" placeholder="₹" value={a.amount} onChange={(e)=>upd("amount",Number(e.target.value))} data-testid={`expense-split-amount-${i}`} />
                      <button type="button" className="col-span-1 text-[#8A2526]" onClick={()=>set("allocations",form.allocations.filter((_,idx)=>idx!==i))}>×</button>
                    </div>
                  );
                })}
                {(form.allocations||[]).length>0 && (
                  <div className="text-[11px] text-[#68665E]">Split total: {formatINR((form.allocations||[]).reduce((s,a)=>s+Number(a.amount||0),0))} of {formatINR(form.amount)}</div>
                )}
              </div>

              <div><label className="input-label">Notes</label><textarea rows={2} className="input-field" value={form.notes} onChange={(e)=>set("notes", e.target.value)} data-testid="expense-notes-input" /></div>

              <div>
                <label className="input-label">Receipt Photo</label>
                <label className="btn-secondary cursor-pointer w-fit">
                  <Upload className="w-4 h-4" />
                  {form.receipt_image ? "Replace photo" : "Upload photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="expense-receipt-input" />
                </label>
                {form.receipt_image && (
                  <img src={form.receipt_image} alt="" className="mt-3 w-32 h-32 object-cover border border-[#E5E3DB]" />
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E3DB]">
                <button type="button" className="btn-secondary" onClick={()=>{setShowForm(false); setEditingId(null);}}>Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary" data-testid="expense-save-button">{saving ? "Saving..." : (editingId ? "Update Expense" : "Save Expense")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8" onClick={()=>setPreview(null)}>
          <img src={preview} alt="receipt" className="max-w-full max-h-full" />
        </div>
      )}

      {docsExpense && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center overflow-y-auto py-8" data-testid="expense-docs-modal">
          <div className="bg-white w-full max-w-xl mx-4">
            <div className="px-6 py-4 border-b border-[#E5E3DB] flex items-center justify-between">
              <div>
                <h2 className="font-headings text-xl font-bold">Receipts on Drive</h2>
                <p className="text-xs text-[#68665E] mt-1">{docsExpense.category} · {formatINR(docsExpense.amount)} · {formatDate(docsExpense.expense_date)}</p>
              </div>
              <button className="btn-ghost" onClick={()=>setDocsExpense(null)} data-testid="expense-docs-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6">
              <DriveDocuments
                category="expense"
                refId={docsExpense.id}
                refLabel={`${docsExpense.category} ${docsExpense.expense_date}`}
                docTypes={["Receipt", "Invoice", "Other"]}
                dateHint={docsExpense.expense_date}
              />
            </div>
          </div>
        </div>
      )}

      <UpiPayModal open={!!payModal} prefill={payModal || {}} onClose={() => setPayModal(null)} />
    </div>
  );
}